'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import { forgotPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { Paragraph } from '../ui/paragraph';
import { MdAlternateEmail } from 'react-icons/md';

export const forgotPasswordSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
  });

export function ForgotPasswordForm({
  onShowSignIn,
  inDialog = false,
}: {
  onShowSignIn?: () => void;
  inDialog?: boolean;
}) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof forgotPasswordSchema>>>({
    resolver: zodResolver(forgotPasswordSchema(formValidationMessages)),
    defaultValues: {
      email: '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      const url = new URL(ROUTES.RESET_PASSWORD(), env.NEXT_PUBLIC_APP_URL);
      await forgotPassword({ ...data, return_url: url.toString(), locale });
      router.push(ROUTES.SIGN_IN());
      toast({
        title: t('text652'),
        variant: 'success',
      });
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text651'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8',
          !inDialog && 'py-14 sm:py-16 lg:py-24',
          'ea-password-reset-form',
        )}
        onSubmit={handleSubmit}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <h4 className="text-center text-[28px] font-medium tracking-tighter sm:text-3xl lg:text-4xl">
            {t('text2345')}
          </h4>
          <Paragraph className="w-full text-center">{t('text3181')}</Paragraph>
        </div>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem required className={cn('w-full', 'ea-password-reset-form__field-email')}>
              <FormControl className="ea-password-reset-form__control">
                <Input
                  {...field}
                  placeholder="Email"
                  before={<MdAlternateEmail size={24} className="text-[#9E9E9E]" />}
                />
              </FormControl>
              <FormMessage className="ea-password-reset-form__message" />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className={cn('w-fit', 'ea-password-reset-form__submit-button')}
          isLoading={form.formState.isSubmitting}
        >
          {t('text185')} <HiOutlineArrowRightCircle size={24} />
        </Button>
        <p
          onClick={inDialog ? onShowSignIn : () => router.push(ROUTES.SIGN_IN())}
          className="mx-auto w-fit cursor-pointer text-center text-sm font-medium text-primary transition-all hover:opacity-80 sm:text-base"
        >
          {t('text3185')}
        </p>
      </form>
    </Form>
  );
}
